import { createContext, useCallback, useContext, useState, ReactNode, useEffect } from 'react';
import Snackbar, { SnackBarProps } from '@brainpool/components/molecules/Snackbar/Snackbar';

interface UserMessage {
  title?: string;
  message: string;
}
export interface LoggerOptions {
  message: string;
  logLevel: SnackBarProps['type'];
  error?: Error;
  context?: Record<string, any>;
  userMessage?: UserMessage;
  duration?: number; // Duration in ms for the snackbar
}

export interface QueuedMessage {
  id: string;
  message: UserMessage;
  logLevel: SnackBarProps['type'];
  timestamp: string;
  duration?: number;
}

export interface LoggerContextType {
  logger: (opts: LoggerOptions) => string;
}

const LoggerContext = createContext<LoggerContextType | undefined>(undefined);

export interface LoggerProviderProps {
  children: ReactNode;
  appContext?: Record<string, any>; // External app context
  newRelicEnabled?: boolean;
  autoHideDelay?: number; // Time in ms before auto-hiding messages
}

export const LoggerProvider = ({
  children,
  appContext = {}, // Will be app context
  newRelicEnabled = true,
  autoHideDelay = 5000, // 5 seconds default
}: LoggerProviderProps) => {
  // Queue of messages to display
  const [messageQueue, setMessageQueue] = useState<QueuedMessage[]>([]);
  // Currently displayed message
  const [activeMessage, setActiveMessage] = useState<QueuedMessage | null>(null);

  // Process the next message in the queue
  useEffect(() => {
    if (!activeMessage && messageQueue.length > 0) {
      // Take the first message from the queue
      const nextMessage = messageQueue[0];

      // Remove it from the queue

      setMessageQueue(prevQueue => prevQueue.slice(1));

      // Set it as the active message
      setActiveMessage(nextMessage);

      // Auto-hide after delay if not an error
      if (nextMessage.logLevel !== 'error') {
        const timer = setTimeout(() => {
          setActiveMessage(null);
        }, autoHideDelay);

        return () => clearTimeout(timer);
      }
    }
  }, [messageQueue, activeMessage, autoHideDelay]);

  const logger = useCallback(
    ({
      message,
      error,
      context: additionalContext = {},
      userMessage,
      logLevel,
      duration = 5000,
    }: LoggerOptions) => {
      const logId = crypto.randomUUID();
      const timestamp = new Date().toISOString();
      const environment = process.env.NODE_ENV;

      // Combine app context with additional context
      const combinedContext = {
        ...appContext,
        ...additionalContext,
        logId,
        message,
        logLevel,
        timestamp,
        environment,
        appUrl: window.location.href,
        duration,
      };
      // Set New Relic custom attribute
      if (!(window as any).__currentLogId) {
        (window as any).__currentLogId = logId;
        (window as any).newrelic?.setCustomAttribute?.('sessionLogId', logId);
      }

      // flatten context for NR
      const flattenedContext = Object.fromEntries(
        Object.entries(combinedContext).map(([k, v]) => [
          k,
          v !== null && typeof v === 'object' ? JSON.stringify(v) : v,
        ])
      );

      // Log to console in development
      if (process.env.NODE_ENV === 'development') {
        console[logLevel === 'error' ? 'error' : 'log'](`[${timestamp}] [${logId}] ${message}`, {
          error,
          combinedContext,
        });
      }

      // Log to New Relic if enabled
      if (newRelicEnabled && process.env.NODE_ENV !== 'development') {
        try {
          (window as any).newrelic?.noticeError(error ?? new Error(message), flattenedContext);
          (window as any).newrelic?.addPageAction?.('ClientLog', flattenedContext);
        } catch (err) {
          console.warn('New Relic logging failed', err);
        }
      }

      // Add to message queue if there's a user message
      if (userMessage) {
        const displayMessage =
          logLevel === 'error' ? `${userMessage.message}\nRef: ${logId}` : userMessage.message;

        setMessageQueue(prevQueue => [
          ...prevQueue,
          {
            id: logId,
            message: { message: displayMessage, title: userMessage.title || logLevel },
            logLevel,
            timestamp,
            duration,
          },
        ]);
      }

      return logId;
    },
    [appContext, newRelicEnabled]
  );

  const handleClose = () => {
    setActiveMessage(null);
  };

  return (
    <LoggerContext.Provider value={{ logger }}>
      {children}
      {activeMessage && (
        <Snackbar
          type={activeMessage.logLevel}
          open={true}
          onClose={handleClose}
          title={activeMessage.message.title}
          message={activeMessage.message.message}
          duration={activeMessage.duration}
        />
      )}
    </LoggerContext.Provider>
  );
};

export const useLogger = () => {
  const ctx = useContext(LoggerContext);
  if (!ctx) throw new Error('useLogger must be used within LoggerProvider');
  return ctx.logger;
};
